import React, { useMemo } from 'react';
import { Badge, BadgeProps } from '@fluentui/react-components';
import { IDependentValues } from '../../../@types/workspaceTypes';
import { IBadgeFormatterOptions } from '../../../@types/editableDataGridTypes';

/**
 * Determines whether a background color is 'dark' to decide whether to use a light or dark text color
 * @param bgColor - to test
 */
const colorIsDark = (bgColor: string) => {
  const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16); // hexToR
  const g = parseInt(color.substring(2, 4), 16); // hexToG
  const b = parseInt(color.substring(4, 6), 16); // hexToB
  return r * 0.299 + g * 0.587 + b * 0.114 <= 150;
};

/**
 * Displays the value in a Fluent UI Badge with customizable colors
 *
 * @param value - the value to display
 * @param dependentValues - data and formatting options
 */
export default function BadgeFormatter({
  value,
  dependentValues,
}: {
  value: string;
  dependentValues: IDependentValues;
}) {
  const options: IBadgeFormatterOptions = dependentValues?.options;
  const badgeProps: BadgeProps | undefined = options?.badgeProps;
  const colorProp: string | ((rowData: any) => string) | undefined = options?.color;

  const color = useMemo(() => {
    let color: string | undefined;
    if (typeof colorProp === 'function') {
      color = colorProp(dependentValues.rowData);
    } else {
      color = colorProp;
    }

    return color;
  }, [colorProp, dependentValues.rowData]);

  const fontColour = useMemo(() => {
    if (!color) {
      return undefined;
    }

    return colorIsDark(color) ? 'white' : 'black';
  }, [color]);

  if (!value) {
    return null;
  }

  return (
    <Badge {...badgeProps} style={{ backgroundColor: color, color: fontColour }} shape="rounded">
      {value}
    </Badge>
  );
}
